import { AxiosError } from 'axios';
import { UseQueryOptions, useQuery } from 'react-query';
import { Circuit } from 'src/types/Circuit';
import { DeviceWithLastSample } from 'src/types/DeviceWithLastSample';
import { Outside } from 'src/types/Outside';
import { Property } from 'src/types/Property';
import { Room, RoomLocation } from 'src/types/Room';
import { get, put } from '../utils';
import { getPage, Page, PageRequest, sortValueFrom } from '../pagination';
import { getPropertyInfo } from './legacy-routes';
import useLog from 'src/hooks/useLog';

export enum PropertyQueryKeys {
  Property = 'Property',
  RoomLocations = 'RoomLocations',
}

export async function searchRooms(query: Partial<Room>): Promise<Room[]> {
  return get('/room', query);
}
export async function getRoomById(id: string): Promise<Room> {
  return get(`/room/${id}`);
}

export async function getPropertyById(id: string): Promise<Property> {
  return get(`/property/${id}`);
}

/** The fields the Property List can be ordered by — see the note in legacy-routes.ts. */
export const PROPERTY_SEARCH_SORT_FIELDS = ['address1', 'customPropertyId', 'createdAt'] as const;

/** How a row is ordered by each of {@link PROPERTY_SEARCH_SORT_FIELDS}. */
export const getPropertySearchSortValue = sortValueFrom<Property>({
  address1: (property) => property.address1,
  customPropertyId: (property) => property.customPropertyId,
  createdAt: (property) => property.createdAt,
});

/**
 * ONE page of a property search.
 *
 * The Property List page searches ACROSS organisations (by address or custom id). It used
 * to walk every page and hand the result to a client-side table, which on a common address
 * fragment is the whole platform in the browser — and a cross-organisation
 * `address1 LIKE '%x%'` is the one query on this endpoint that scans every property there
 * is, so pulling all of it was the worst possible shape.
 */
export async function searchPropertiesPage(
  query: Partial<Pick<Property, 'address1' | 'customPropertyId' | 'organisationId'>>,
  request: PageRequest
): Promise<Page<Property>> {
  return getPage<Property>('/property', request, query, {
    // The legacy branch (`searchPropertiesInternal`) reads id, address1, organisationId,
    // customPropertyId and tag — it has no ORDER BY at all, so with the flag off the rows
    // arrive in whatever order MySQL produced and the table's sort indicator would be
    // describing an ordering that did not happen. Across pages that is worse than
    // cosmetic: an unordered array windowed by offset can repeat a row and skip another.
    sortValue: getPropertySearchSortValue,
  });
}

interface InstallationDetail {
  installationId: string;
  name: string;
  type: string;
  value: string;
}

export async function getPropertyDetails(installationId: string): Promise<InstallationDetail[]> {
  return get(`property/${installationId}/detail/`);
}

/**
 * One page of a property search, as a query.
 *
 * Defaults to the API's maximum page rather than every page: a caller that needs more than
 * 200 matching properties wants a narrower search, not a longer download.
 */
export function useSearchProperties(
  query: Partial<Pick<Property, 'address1' | 'customPropertyId' | 'organisationId'>>,
  request: PageRequest = { limit: 200, offset: 0 },
  options?: UseQueryOptions<Page<Property>, AxiosError>
) {
  return useQuery<Page<Property>, AxiosError>(
    [PropertyQueryKeys.Property, query, request],
    () => searchPropertiesPage(query, request),
    {
      // The previous page stays on screen while the next one loads, so paging dims the
      // table instead of emptying it.
      keepPreviousData: true,
      ...options,
    }
  );
}

export type InstallationLocationParentSearchResults = {
  type: 'OUTSIDE' | 'ROOMLOCATION' | 'CIRCUIT';
  roomLocation?: RoomLocation;
  circuit?: Circuit;
  outside?: Outside;
};

export async function searchByInstallationLocationIdForParent(
  installationLocationId: string
): Promise<InstallationLocationParentSearchResults> {
  const query = { installationLocationId };

  const [roomLocations, circuits, outsides] = await Promise.all([
    get('/roomlocation', query),
    get('/circuit', query),
    get('/outside', query),
  ]);

  if (roomLocations.length > 0) {
    return {
      type: 'ROOMLOCATION',
      roomLocation: roomLocations[0],
    };
  }
  if (circuits.length > 0) {
    return {
      type: 'CIRCUIT',
      circuit: circuits[0],
    };
  }
  if (outsides.length > 0) {
    return {
      type: 'ROOMLOCATION',
      outside: outsides[0],
    };
  }

  throw new Error('Not found');
}

export async function getDeviceForRoomLocation(propertyId: string, roomLocationId: string): Promise<DeviceWithLastSample> {
  const propInfo = await getPropertyInfo(propertyId);
  const roomLocation = propInfo.rooms.flatMap((r) => r.roomLocations).find((rl) => rl.id === roomLocationId);
  return roomLocation.device;
}

export async function getRoomLocationById(id): Promise<RoomLocation> {
  const res = await get<RoomLocation[]>('roomLocation/', {
    id,
  });

  console.log({ res });
  if (res.length) {
    return res[0];
  }

  return null;
}

export async function updateRoomLocation(roomLocation: Partial<RoomLocation> & { id: string }): Promise<RoomLocation> {
  return put<RoomLocation>(`roomLocation/${roomLocation.id}`, roomLocation as any);
}

export async function searchRoomLocations({ roomId }: { roomId: string }): Promise<RoomLocation[]> {
  return await get<RoomLocation[]>('roomLocation/', { roomId });
}

export function useRoomLocations(roomId?: string, options?: UseQueryOptions<RoomLocation[], AxiosError>) {
  const log = useLog();

  return useQuery<RoomLocation[], AxiosError>([PropertyQueryKeys.RoomLocations, roomId], () => searchRoomLocations({ roomId: roomId! }), {
    enabled: Boolean(roomId),
    onError: (err) => {
      if (err instanceof Error) {
        log.error(`Failed to load room locations${roomId ? ` for room ${roomId}` : ''}: ${err.message}`, err);
      } else {
        log.error('Failed to load room locations');
      }
    },
    ...options,
  });
}

export type CombinedRoomRadarSamplesQuery = {
  fromTimestamp: string;
  toTimestamp: string;
  deviceIds: string;
  timezone: string;
  occupancyResetTime?: string;
  filterMetrics?: string;
  withDeviceSamples?: string;
};

export type CombinedRoomRadarSample = {
  deviceId: string;
  timestamp: string;
  isOccupancyReset?: boolean;
  sample: {
    rawOccupancy: number;
    occupancy: number;
    occupancyDelta: number;
    totalIn: number;
    totalOut: number;
    periodIn: number;
    periodOut: number;
  };
  deviceSamples: Record<
    string,
    {
      totalIn: number;
      totalOut: number;
      rawOccupancy: number;
      periodIn: number;
      periodOut: number;
    }
  >;
};

export async function getRoomRadarCombinedSamples(query: CombinedRoomRadarSamplesQuery): Promise<CombinedRoomRadarSample[]> {
  return get('room/samples/raw', query);
}
